import React, { useContext, useEffect } from "react";
import FilterPopup from "../FilterPopup";
import { Button } from "semantic-ui-react";
import { formatLongDate, formatWrittenDayMonthYear } from "utilities/momentutils";
import PeriodSelect from "../periodfilter/PeriodSelect";
import { type PeriodFilterType, initialPeriodFilter } from "./FilterTypes/PeriodFilterType";
import moment from "moment";
import ButtonGroup from "components/Atoms/ButtonGroup";
import { focusOnMount } from "utilities/reactUtils";
import { FilterContext } from "../FilterContext";

interface Props {
  targets?: server.dto.PeriodFilterTargets[]
}

const areEqual = (a: PeriodFilterType, b: PeriodFilterType): boolean => {
  if (a.period === "unknown" && b.period === "unknown") return true;

  if (a.period !== b.period) return false;

  const periodsWithoutStartEndDates: server.dto.PeriodFilterOptions[] = [
    "unknown",
    "currentMonth",
    "currentQuarter",
    "currentWeek",
    "currentYear",
    "fromToday"
  ];

  if (!periodsWithoutStartEndDates.includes(a.period)) {
    if (!a.selectionStart?.isSame(b.selectionStart)) return false;
    if (!a.selectionEnd?.isSame(b.selectionEnd)) return false;
  }

  if (a.target !== b.target) return false;

  return true;
};

const PeriodFilter = (props: Props) => {
  const title = RESX.DateTime.Period;

  const { register, getValue, setInitialValue, setValue } = useContext(FilterContext);
  register("period", {
    getTitle: () => title,
    renderSelection: (filter) => <>{formatFilterText(filter, props.targets != null && props.targets.length > 0)}</>,
    hasValue: (filter) => filter.period != null && filter.period !== "unknown",
    reset: () => initialPeriodFilter,
    areEqual
  });
  useEffect(() => {
    setInitialValue("period", initialPeriodFilter);
  }, []);

  const [popupOpen, setPopupOpen] = React.useState<boolean>(false);
  const [period, setPeriod] = React.useState<server.dto.PeriodFilterOptions>(initialPeriodFilter.period ?? "currentYear");
  const [target, setTarget] = React.useState<server.dto.PeriodFilterTargets>(initialPeriodFilter.target ?? props.targets?.[0]);
  const [startMoment, setStartMoment] = React.useState<moment.Moment>(initialPeriodFilter.selectionStart || moment().utc());
  const [endMoment, setEndMoment] = React.useState<moment.Moment>(initialPeriodFilter.selectionEnd || moment().utc().add(1, "day"));
  const [apply, setApply] = React.useState<boolean>(false);

  const onPopupClose = () => {
    setPopupOpen(false);
  };

  const onPopupOpen = () => {
    const value = getValue("period");
    setPeriod(value.period ?? "currentYear");
    setTarget(value.target ?? props.targets?.[0]);
    setStartMoment(value.selectionStart || moment().utc());
    setEndMoment(value.selectionEnd || moment().utc().add(1, "day"));
    setPopupOpen(true);
  };

  React.useEffect(() => {
    if (apply) {
      onApply();
      setApply(false);
      setPopupOpen(false);
    }
  }, [apply]);

  const onApply = () => {
    // Don't include time periods that are not used
    const selectionStart = ["untilAndIncluding", "currentYear", "currentQuarter", "currentMonth", "currentWeek", "fromToday"].includes(period) ? undefined : startMoment;
    const selectionEnd = ["untilAndIncluding", "fromUntilAndIncluding"].includes(period) ? endMoment : undefined;

    setValue("period", {
      period,
      target,
      selectionStart,
      selectionEnd
    });
    setApply(false);
    setPopupOpen(false);
  };

  return <div>
    <FilterPopup
      filterButtonLabel={title}
      content={<>
        {props.targets && (<div className="margin-bottom">
          <ButtonGroup.Group>
            {props.targets.map((t, idx) => (
              <ButtonGroup.Button
                key={t}
                group="targets"
                onClick={() => { setTarget(t); }}
                active={target === t}
                type="secondary"
                otherProps={{
                  ref: idx === 0 ? focusOnMount : undefined
                }}
              >
                {formatFilterTextWithTarget(t)}
              </ButtonGroup.Button>
            ))}
          </ButtonGroup.Group>
        </div>)}
        <div className="margin-bottom">
          <PeriodSelect
            value={period}
            initialStart={startMoment}
            initialEnd={endMoment}
            onChange={(period, startMoment, endMoment, closeFilter) => {
              setPeriod(period);
              setStartMoment(startMoment);
              setEndMoment(endMoment);
              if (closeFilter) {
                setApply(true);
              }
            }}
            onQuickSelect={(period) => {
              setPeriod(period);
              setApply(true);
            }}
            autoFocus
          />
        </div>

        <Button onKeyDown={(e) => {
          // We need to preven the default event here as the enter key triggers an on click as well
          e.preventDefault();
        }} onClick={() => setApply(true)} fluid primary>
          {RESX.SelectionManager.Apply}
        </Button>
      </>}
      width={400}
      open={popupOpen}
      onClose={onPopupClose}
      onOpen={onPopupOpen}
    />
  </div>;
};

export default PeriodFilter;

const formatFilterText = (filter: PeriodFilterType, hasTargets: boolean) => {
  const timePeriod = formatFilterTextTimePeriod(filter);

  if (hasTargets) {
    return `${timePeriod} (${formatFilterTextWithTarget(filter.target)})`;
  }
  return timePeriod;
};

const formatFilterTextWithTarget = (target: server.dto.PeriodFilterTargets) => {
  switch (target) {
    case "sendDate": return RESX.DateTime.Send;
    case "dueDate": return RESX.DateTime.Due;
    case "customerChurnDate": return "Churn";
    case "customerSinceDate": return "Customer since";
    case "byOverlap": return "By overlap";
  }
};

const formatFilterTextTimePeriod = (filter: PeriodFilterType) => {
  const { period, selectionStart, selectionEnd } = filter;
  switch (period) {
    case "currentYear": return RESX.DateTime.CurrentYear;
    case "currentQuarter": return RESX.DateTime.CurrentQuarter;
    case "currentMonth": return RESX.DateTime.CurrentMonth;
    case "currentWeek": return RESX.DateTime.CurrentWeek;
    case "perYear": return `${RESX.DateTime.PerYear} - ${selectionStart.year()}`;
    case "perQuarter": {
      const quarter = selectionStart.quarter();
      const quarters = {
        1: RESX.DateTime.Quarter1,
        2: RESX.DateTime.Quarter2,
        3: RESX.DateTime.Quarter3,
        4: RESX.DateTime.Quarter4
      };

      return `${quarters[quarter]} ${selectionStart.format("YYYY")}`;
    }
    case "perMonth": return `${selectionStart.format("MMMM YYYY")}`;

    case "perWeek": return `${RESX.DateTime.Week} ${selectionStart.week()} ${selectionStart.format("YYYY")}`;

    case "perDay": return formatWrittenDayMonthYear(selectionStart);

    case "from": return `${RESX.DateTime.From} ${formatLongDate(selectionStart)}`;

    case "fromToday": return `${RESX.DateTime.FromToday}`;

    case "untilAndIncluding": return `${RESX.DateTime.UntilAndIncluding} ${formatLongDate(selectionEnd)}`;

    case "fromUntilAndIncluding": return `${selectionStart.format("ll")} ${RESX.DateTime.UntilAndIncludingAbbreviated} ${selectionEnd.format("ll")}`;
  }
};